import polyglotI18nProvider from 'ra-i18n-polyglot';
import { TranslationMessages, resolveBrowserLocale } from 'react-admin';
import { en } from './en';
import { nl } from './nl';

const messages: { [key: string]: TranslationMessages } = {
  en,
  nl,
};

const missingKeys: FlatStructure = {};

/**
 * A string shaped like a translation key: dot-separated segments of key-safe characters, at least
 * one dot, no whitespace, no empty segment. Every leaf in `en.ts`/`nl.ts` is dotted (`custom.…`,
 * `resources.…`, `ra.…`, `validation.…`), so nothing real is filtered out by requiring it.
 *
 * **Deliberately a second copy** of the regex at `src/providers/data/index.ts:450`, which is not
 * exported. Exporting it would make this module — imported by `App.tsx` and by i18n tests that
 * touch no network — pull in the whole data provider, `providers/auth` and axios at module-eval
 * time, for a regex. The two are independent by intent: one decides whether a **Laravel 422
 * message** is a key, this one decides whether a **missing lookup** is worth recording. If a third
 * copy ever appears, that is the point to lift it into a shared constant rather than now.
 */
const TRANSLATION_KEY = /^[A-Za-z0-9_-]+(?:\.[A-Za-z0-9_-]+)+$/;

/**
 * TVR-2150 — node-polyglot reads this off its **constructor options** (`opts.onMissingKey`,
 * node-polyglot 2.6.0 `Polyglot`), never off the phrases object. It used to live in the phrases
 * returned by the locale getter, where `Polyglot.prototype.extend` simply stored it as a phrase
 * named `_` that `t` never consulted — so `missingKeys` stayed empty and the reporter below
 * printed `{}` for its whole life.
 *
 * Returning the key keeps the behaviour `allowMissing: true` gave us (node-polyglot prefers
 * `onMissingKey` over the `allowMissing` path once one is supplied): a missing key renders as
 * itself, never as a throw, an empty string or `undefined`. Call sites rely on that — see
 * `src/components/DialogContainer/EditSubItem.tsx`.
 *
 * Note this only fires when nothing else resolves the key: a per-call `_` default, which
 * react-admin's `useGetResourceLabel` and every `<FieldTitle>` supply, wins over it in
 * `Polyglot.prototype.t` and is therefore invisible here by design.
 *
 * **Recording is gated on the key shape; returning is not.** `translate` is not only reached from
 * `translate()` — react-admin funnels `notify(message)` through it too
 * (`ra-ui-materialui/src/layout/Notification.tsx:161`), and three call sites pass free text rather
 * than a key: a Laravel 422 message (`units/components/BatchAssignModal.tsx:183`) and two raw
 * `error.message` values (`components/GoogleAuthButton.tsx:118,135`,
 * `units/components/RevokeKeysBulkAction.tsx:43`). A server sentence is not a missing key, and
 * storing it would put it in a module-scope map for the life of the tab, re-serialised to the
 * console by the reporter below every ten seconds — repeating whatever the message happened to
 * carry, a serial number or a person's name included. So only a dotted key is recorded. Every
 * input is still **returned verbatim**, which is what AC2 and the call sites depend on.
 */
const onMissingKey = (key: string): string => {
  if (TRANSLATION_KEY.test(key)) {
    missingKeys[key] = key;
  }

  return key;
};

/** A snapshot of the keys no catalogue could resolve so far, for the reporter and for tests. */
export const getMissingKeys = (): FlatStructure => ({ ...missingKeys });

export const i18nProvider = polyglotI18nProvider(
  (locale) => messages[locale] || messages.en,
  resolveBrowserLocale(),
  [
    { locale: 'en', name: 'English' },
    { locale: 'nl', name: 'Nederlands' },
  ],
  {
    allowMissing: true,
    onMissingKey,
  },
);
interface FlatStructure {
  [key: string]: string;
}

interface HierarchicalStructure {
  [key: string]: HierarchicalStructure | string;
}

function convertToHierarchy(obj: FlatStructure): HierarchicalStructure {
  const result: HierarchicalStructure = {};

  for (const key in obj) {
    const keys = key.split('.');
    let current: HierarchicalStructure | string = result;

    for (let i = 0; i < keys.length; i++) {
      if (i === keys.length - 1) {
        (current as HierarchicalStructure)[keys[i]] = obj[key];
      } else {
        if (typeof current[keys[i]] === 'string' || typeof current[keys[i]] === 'undefined') {
          current[keys[i]] = {};
        }
        current = current[keys[i]] as HierarchicalStructure;
      }
    }
  }
  return result;
}

// Only in development mode and every 10 seconds
if (process.env.NODE_ENV === 'development') {
  setInterval(() => console.debug(JSON.stringify(convertToHierarchy(missingKeys))), 10 * 1000);
}
